import { Box, MenuItem, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import Form from "@rjsf/mui";
import { RJSFSchema, UiSchema, WidgetProps } from "@rjsf/utils";
import { customizeValidator } from "@rjsf/validator-ajv8";
import * as R from "ramda";
import React, { useEffect, useState } from "react";
import { getChartInputOptions } from "../../../api/chart";
import { Chart, ChartSourceData } from "../../../types/chart";
import { ChartBlock } from "../../../types/notebook/note/block/chart";

// Chart Inputs (query-omega-oql/pages/oql/chart-block.html "Chart Input Schema"): a chart's input-schema is a
// JsonSchema Record; react-jsonschema-form renders it above the chart. "month" is not a standard JSON Schema
// format, so it is registered here (value "YYYY-MM"); "x-options" names a Query whose rows are a select's
// choices (Qo.Api.Chart/input-options-by-id). A viewer's values are Input Values: passed per request, never saved.
const validator = customizeValidator({
  customFormats: { month: /^\d{4}-(0[1-9]|1[0-2])$/ },
});

const inputName = (id: string) => id.replace(/^root_/, "");

// Material UI's month picker (MUI X DatePicker, year + month views), not <input type="month">: Firefox renders a native
// month input as a plain text box (U.3). The value stays "YYYY-MM" -- the "month" format registered above.
export const MonthWidget: React.FC<WidgetProps> = ({ id, label, value, onChange, disabled, readonly }) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      label={label}
      views={["year", "month"]}
      openTo="month"
      format="YYYY-MM"
      value={value ? dayjs(`${value}-01`) : null}
      disabled={disabled || readonly}
      onChange={(d) => onChange(d && d.isValid() ? d.format("YYYY-MM") : undefined)}
      slotProps={{
        textField: { id, size: "small", inputProps: { "data-testid": `chart-input-${inputName(id)}` } },
      }}
    />
  </LocalizationProvider>
);

export const QueryOptionsWidget: React.FC<WidgetProps> = ({
  id,
  label,
  value,
  onChange,
  disabled,
  readonly,
  formContext,
}) => {
  const block: ChartBlock = formContext.block;
  const name = inputName(id);
  const [rows, setRows] = useState<unknown[][]>([]);
  useEffect(() => {
    getChartInputOptions({
      "app-id": block["app-id"],
      "page-id": block["page-id"],
      "block-id": block["block-id"],
      name,
    })
      .then((d: ChartSourceData) => setRows(d.rows))
      .catch((e) => console.error("FAILED_TO_FETCH_CHART_INPUT_OPTIONS", e));
  }, [block["app-id"], block["page-id"], block["block-id"], name]);
  return (
    <TextField
      id={id}
      label={label}
      select
      size="small"
      sx={{ minWidth: 160 }}
      value={rows.length ? value ?? "" : ""}
      disabled={disabled || readonly}
      SelectProps={{ SelectDisplayProps: { "data-testid": `chart-input-${name}` } as any }}
      onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
    >
      {rows.map((row) => (
        <MenuItem key={String(row[0])} value={String(row[0])}>
          {String(row[1] ?? row[0])}
        </MenuItem>
      ))}
    </TextField>
  );
};

// A ui:widget per property from the schema itself: month -> MonthWidget, x-options -> QueryOptionsWidget.
const uiSchemaFor = (schema: RJSFSchema): UiSchema => {
  const props = (schema.properties ?? {}) as Record<string, any>;
  const ui: UiSchema = { "ui:submitButtonOptions": { norender: true } };
  for (const [name, prop] of Object.entries(props)) {
    if (prop["x-options"]) ui[name] = { "ui:widget": "QueryOptionsWidget" };
    else if (prop.format === "month") ui[name] = { "ui:widget": "MonthWidget" };
  }
  return ui;
};

export const ChartInputs: React.FC<{
  block: ChartBlock;
  chart: Chart;
  onChange: (values: Record<string, unknown>) => void;
}> = ({ block, chart, onChange }) => {
  const schema = chart["input-schema"]?.schema as RJSFSchema | undefined;
  const declared = Object.keys((schema?.properties ?? {}) as Record<string, unknown>);
  // Defaults are the stored source.input, limited to declared keys: an undeclared key would be rejected
  // (QO_CHART_UNDECLARED_INPUT).
  const [values, setValues] = useState<Record<string, unknown>>(
    R.pick(declared, (chart.source?.input ?? {}) as Record<string, unknown>) as Record<string, unknown>,
  );
  if (!schema) return null;
  return (
    <Box data-testid="chart-inputs" sx={{ mb: 1 }}>
      <Form
        schema={schema}
        uiSchema={uiSchemaFor(schema)}
        validator={validator}
        formData={values}
        formContext={{ block }}
        widgets={{ MonthWidget, QueryOptionsWidget }}
        liveValidate
        showErrorList={false}
        onChange={(e) => {
          setValues(e.formData);
          if (e.errors.length === 0) onChange(e.formData);
        }}
      />
    </Box>
  );
};
